/**
 * EventHostCTAsPanel -- the Calls to Action tab of a class, table, seminar or speaking engagement (owner 2026-10-29).
 *
 * These events have no event CTA, landing page, flyer or emails. The tab lists, READ-ONLY, every active CTA of the
 * event's venue and host organization(s), with the organization's own CTA ids (get_event_host_ctas): at this venue
 * first, then organization-wide, then the organization's other locations, newest first. Each card is the
 * organization page's card with canEdit={false}: copy the link and download the QR, nothing editable. Signups from
 * these CTAs count toward the event when they happen at its place and time (close rate, migration 1260).
 */
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QrCodeIcon } from "@heroicons/react/24/outline";
import OrganizationCTACard from "../organizations/OrganizationCTACard";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  getEventHostCTAs,
  getEventHostOrganizations,
  type EventHostCTAScope,
} from "../../services/eventHostCTAs.service";
import type { Organization } from "../../types/organization.types";
import { eventRunNoun } from "../../utils/eventTypePolicy";

const SCOPE_LABEL: Record<EventHostCTAScope, string> = {
  venue: "At this venue",
  organization: "Organization-wide",
  organization_location: "Another location of this organization",
};

const SCOPE_STYLE: Record<EventHostCTAScope, string> = {
  venue: "bg-green-100 text-green-800",
  organization: "bg-blue-100 text-blue-800",
  organization_location: "bg-gray-100 text-gray-700",
};

export const eventHostCTAKeys = {
  list: (eventId: string) => ["event-host-ctas", eventId] as const,
  orgs: (eventId: string) => ["event-host-orgs", eventId] as const,
};

interface Props {
  event: { id: string; type?: string | null };
}

const EventHostCTAsPanel: React.FC<Props> = ({ event }) => {
  const noun = eventRunNoun(event.type);
  const {
    data: rows,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: eventHostCTAKeys.list(event.id),
    queryFn: () => getEventHostCTAs(event.id),
  });
  const { data: hosts } = useQuery({
    queryKey: eventHostCTAKeys.orgs(event.id),
    queryFn: () => getEventHostOrganizations(event.id),
  });

  return (
    <div className="space-y-4" data-testid="event-host-ctas">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium">
          This {noun} has no QR code or link of its own.
        </p>
        <p className="mt-1">
          Use the venue&apos;s QR codes below. Sign-ups from them during the{" "}
          {noun} (from its start until an hour after it ends) count toward this
          event. They are managed on the organization&apos;s page; here you can
          copy a link or download a QR code.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          The venue&apos;s QR codes could not be loaded.{" "}
          {error instanceof Error ? error.message : ""}
        </div>
      ) : !rows || rows.length === 0 ? (
        <div
          className="rounded-lg border border-gray-200 bg-white p-10 text-center"
          data-testid="event-host-ctas-empty"
        >
          <QrCodeIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
          {hosts && hosts.length > 0 ? (
            <>
              <h4 className="text-base font-medium text-gray-900">
                The venue has no active QR codes yet
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                QR codes for a {noun} come from the venue&apos;s organization
                page. Add or activate one there:
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {hosts.map((h) => (
                  <li key={h.id}>
                    <Link
                      to={`/organizations/${h.id}`}
                      className="text-primary-600 hover:underline"
                    >
                      {h.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <h4 className="text-base font-medium text-gray-900">
                No venue linked to this {noun}
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                QR codes for a {noun} come from the venue&apos;s organization
                page. Link the venue (organization / location) on the Overview
                tab to see its QR codes here.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.cta.id} data-testid="event-host-cta">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`rounded px-2 py-0.5 font-medium ${SCOPE_STYLE[r.scope]}`}
                >
                  {SCOPE_LABEL[r.scope]}
                </span>
                <Link
                  to={`/organizations/${r.organization.id}`}
                  className="text-gray-600 hover:underline"
                >
                  {r.organization.name}
                </Link>
              </div>
              <OrganizationCTACard
                cta={r.cta}
                organization={r.organization as unknown as Organization}
                onUpdate={() => void refetch()}
                canEdit={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventHostCTAsPanel;
